import { FunctionComponent, memo, useCallback, useMemo, useState } from "react";
import { IProps } from "./phrase-triggers-results-table.types";
import {
    Box,
    BoxContent,
    BoxHeader,
    BoxHeading,
    Column,
    Heading,
    Table,
    TableColumns,
    Cell,
    NumericValue,
    TableMenuSection,
} from "@edgetier/client-components";
import { Button, SpinnerUntil, Tooltip } from "@edgetier/components";
import { IPhraseTriggerResult, Route, SearchType } from "@edgetier/watchtower-types";
import { roundTo } from "utilities/round-to";
import usePhraseTrigger from "hooks-for/phrase-triggers/use-phrase-trigger";
import DeletePhraseTriggerResult from "./delete-phrase-trigger-result";
import { useModal } from "@edgetier/modal";
import PhraseTriggerResultsInteractionPreview from "components-for/phrase-triggers/phrase-trigger-results-interaction-preview";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { PHRASE_TRIGGER_REPORT_PREFIX } from "../phrase-triggers-performance-report.constants";
import { PHRASE_TRIGGER_RESULTS_LIMIT } from "./phrase-triggers-results-table.constants";
import { useInfiniteQuery } from "react-query";
import {
    generateSimilarityGradient,
    requestPhraseTriggerResults,
    setLanguageToPhraseTriggerResult,
    usePhraseTriggerResultsLanguages,
} from "./phrase-triggers-results-table.utilities";
import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons";
import UrlTableMenuSortOptions from "components-for/tables/url-table-menu-sort-options";
import { isOrderedByProperty } from "utilities/is-ordered-by-property";
import { isOrderedByAscending } from "utilities/is-ordered-by-ascending";
import usePhraseMessageTranslation from "hooks-for/translations/use-phrase-message-translation";
import TranslationButton from "components-for/translations/translation-button";
import PhraseTriggerTranslationSentence from "components-for/phrase-triggers/phrase-trigger-translation-sentence";
import "./phrase-triggers-results-table.scss";
import SimilarityThresholdTooltip from "./similarity-threshold-tooltip";

/**
 * A table showing all the phrase triggers results
 * @param props.phraseTriggerId
 */
const PhraseTriggersResultsTable: FunctionComponent<IProps> = ({ phraseTriggerId, filters }) => {
    // Retrieve phrase details
    const { data: phraseTrigger } = usePhraseTrigger(phraseTriggerId);

    const { startDateTime, endDateTime, orderBy } = useInteractionQueryParameters({
        defaults: {
            startDateTime: filters.startDateTime,
            endDateTime: filters.endDateTime,
        },
        prefix: PHRASE_TRIGGER_REPORT_PREFIX,
        isPrefixOptional: true,
    });

    const [visiblePhraseResult, setVisiblePhraseResult] = useState<IPhraseTriggerResult | null>(null);
    const { openModal, modalProps } = useModal({ onCloseModal: () => setVisiblePhraseResult(null) });

    // Translations for current phrase trigger results messages
    const { activeTranslations, languagesByIsoCode, userLanguage, isTranslationEnabled, toggleTranslation } =
        usePhraseMessageTranslation();

    const phraseTriggerResultsParameters = {
        limit: PHRASE_TRIGGER_RESULTS_LIMIT,
        phraseTriggerId,
        interactionStartDateTime: startDateTime,
        interactionEndDateTime: endDateTime,
        orderBy: orderBy,
    };

    // Retrieve phrase trigger results
    const {
        hasNextPage,
        data,
        fetchNextPage,
        isFetchingNextPage,
        isLoading: isPhraseTriggerResultsLoading,
    } = useInfiniteQuery(
        [Route.PhraseTriggersResults, phraseTriggerResultsParameters],
        ({ pageParam }) =>
            requestPhraseTriggerResults(Route.PhraseTriggersResults, phraseTriggerResultsParameters, pageParam),
        { getNextPageParam: ({ pagination }) => pagination.nextPage }
    );

    const phraseTriggerResults = useMemo(
        () => (data?.pages ?? []).flatMap<IPhraseTriggerResult>((results) => results.items),
        [data]
    );

    // Request languages for each phrase trigger result
    const { phraseTriggerResultsLanguages, isPhraseTriggerResultsLanguagesLoading } =
        usePhraseTriggerResultsLanguages(phraseTriggerResults);

    /**
     * Load more samples if there are some available.
     */
    const requestMoreSamples = useCallback(() => {
        fetchNextPage();
    }, [fetchNextPage]);

    /**
     * Open an interaction in a modal from a phrase trigger result
     * @param phraseTriggerResult Selected phrase result
     */
    const openInteractionInModal = (phraseTriggerResult: IPhraseTriggerResult) => {
        // Remove tracking here to stop sending multiple tracking events for each of re-rendering
        setVisiblePhraseResult(phraseTriggerResult);
        openModal();
    };

    return (
        <>
            {visiblePhraseResult !== null && (
                <PhraseTriggerResultsInteractionPreview
                    phraseResult={visiblePhraseResult}
                    phraseResults={phraseTriggerResults ?? []}
                    {...modalProps}
                />
            )}
            <Box className="phrase-triggers-results-table">
                <BoxHeader>
                    <BoxHeading>
                        <div className="phrase-triggers-results-table__heading">
                            <span className="phrase-triggers-results-table__heading__title">
                                Messages Tagged by Trigger
                            </span>
                        </div>
                    </BoxHeading>
                </BoxHeader>

                <BoxContent>
                    <SpinnerUntil<IPhraseTriggerResult[]>
                        data={setLanguageToPhraseTriggerResult(
                            phraseTriggerResults ?? [],
                            phraseTriggerResultsLanguages
                        )}
                        isReady={!isPhraseTriggerResultsLoading || isPhraseTriggerResultsLanguagesLoading}
                    >
                        {(data) => (
                            <div className="phrase-triggers-results-table__content">
                                <Table<IPhraseTriggerResult>
                                    getRowKey={({ phraseTriggerResultId }) => phraseTriggerResultId}
                                    rows={data}
                                    onRowClick={(phraseTriggerResultRow) =>
                                        openInteractionInModal(phraseTriggerResultRow)
                                    }
                                    isInBox
                                    headerIsSticky
                                    className="phrase-triggers-results-table__table"
                                >
                                    <TableColumns>
                                        <Column>
                                            <Heading>Remove Tag</Heading>
                                            <Cell<IPhraseTriggerResult>>
                                                {({ phraseTriggerResultId }) => (
                                                    <DeletePhraseTriggerResult
                                                        phraseTriggerResultId={phraseTriggerResultId}
                                                    />
                                                )}
                                            </Cell>
                                        </Column>
                                        {isTranslationEnabled &&
                                            !data.every(
                                                (phraseTriggerResult) =>
                                                    phraseTriggerResult.languageIsoCode === userLanguage.languageIsoCode
                                            ) && (
                                                <Column>
                                                    <Heading>Language</Heading>
                                                    <Cell<IPhraseTriggerResult>>
                                                        {({ languageIsoCode, phraseTriggerResultId, sentence }) => (
                                                            <>
                                                                {typeof languageIsoCode === "string" &&
                                                                    userLanguage.languageIsoCode !==
                                                                        languageIsoCode && (
                                                                        <TranslationButton
                                                                            isTranslated={
                                                                                typeof activeTranslations[
                                                                                    phraseTriggerResultId
                                                                                ] !== "undefined"
                                                                                    ? activeTranslations[
                                                                                          phraseTriggerResultId
                                                                                      ].isTranslated
                                                                                    : false
                                                                            }
                                                                            onClick={() =>
                                                                                toggleTranslation(
                                                                                    phraseTriggerResultId,
                                                                                    sentence,
                                                                                    languageIsoCode
                                                                                )
                                                                            }
                                                                            textLanguage={
                                                                                languagesByIsoCode[languageIsoCode]
                                                                            }
                                                                            userLanguage={userLanguage}
                                                                        />
                                                                    )}
                                                            </>
                                                        )}
                                                    </Cell>
                                                </Column>
                                            )}
                                        <Column>
                                            <Heading className="phrase-triggers-results-table__sentence">
                                                Sentence
                                            </Heading>
                                            <Cell<IPhraseTriggerResult>>
                                                {({ sentence, phraseTriggerResultId }) => (
                                                    <PhraseTriggerTranslationSentence
                                                        sentence={sentence}
                                                        activeTranslation={activeTranslations[phraseTriggerResultId]}
                                                    />
                                                )}
                                            </Cell>
                                        </Column>
                                        {phraseTrigger?.searchTypeId === SearchType.Similar && (
                                            <Column>
                                                <Heading
                                                    isSorted={isOrderedByProperty("similarity", orderBy)}
                                                    isSortedAscending={isOrderedByAscending("similarity", orderBy)}
                                                    menu={
                                                        <TableMenuSection>
                                                            <UrlTableMenuSortOptions
                                                                property="similarity"
                                                                queryParameterPrefix={PHRASE_TRIGGER_REPORT_PREFIX}
                                                            />
                                                        </TableMenuSection>
                                                    }
                                                >
                                                    Similarity
                                                </Heading>
                                                <Cell<any>>
                                                    {({ similarity }) => {
                                                        const similarityThreshold =
                                                            typeof phraseTrigger === "undefined"
                                                                ? 0
                                                                : roundTo(phraseTrigger.similarityThreshold * 100, 2);
                                                        const similarityPercentage = roundTo(similarity * 100, 2);
                                                        return (
                                                            <div className="phrase-triggers-results-table__threshold">
                                                                <Tooltip
                                                                    content={
                                                                        <SimilarityThresholdTooltip
                                                                            similarityPercentage={similarityPercentage}
                                                                            similarityThreshold={similarityThreshold}
                                                                        />
                                                                    }
                                                                    useArrow
                                                                >
                                                                    <div className="phrase-triggers-results-table__threshold-bar-container">
                                                                        <div
                                                                            className="phrase-triggers-results-table__threshold-bar"
                                                                            style={{
                                                                                background: generateSimilarityGradient(
                                                                                    similarityThreshold,
                                                                                    similarityPercentage
                                                                                ),
                                                                            }}
                                                                        >
                                                                            <div
                                                                                className="phrase-triggers-results-table__threshold-bar-divider"
                                                                                style={{
                                                                                    width: `${similarityThreshold}%`,
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </Tooltip>
                                                                <div>
                                                                    <NumericValue
                                                                        value={similarityPercentage}
                                                                        options={{
                                                                            minimumFractionDigits: 2,
                                                                            maximumFractionDigits: 2,
                                                                        }}
                                                                        unit="%"
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    }}
                                                </Cell>
                                            </Column>
                                        )}
                                    </TableColumns>
                                </Table>
                                {hasNextPage && (
                                    <div className="phrase-trigger-samples__actions">
                                        <Button
                                            icon={faEllipsisV}
                                            outline
                                            isLoading={isFetchingNextPage !== false}
                                            onClick={requestMoreSamples}
                                        >
                                            Load More Results
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </SpinnerUntil>
                </BoxContent>
            </Box>
        </>
    );
};

export default memo(PhraseTriggersResultsTable);
